import * as React from 'react';

import {
  AsyncBoundary,
  Button,
  Col,
  MultiSelect,
  Row,
  Select,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideBuilding } from 'lucide-react';

import * as locationGroupFormState from '../../_state/locationGroupForm.state';
import * as locationsState from '../../_state/locations.state';
import { FormSection } from '../_shared/FormSection';
import { SettingsContent } from '../_shared/SettingsContent';

export const LocationGroupInfo = () => {
  const toast = useToast();
  const currentFacilityGroup = useAtomValue(
    locationsState.$currentFacilityGroup,
  );
  const groupId = useAtomValue(locationsState.$groupId);
  const form = useAtomValue(locationGroupFormState.$locationGroupForm);
  const updateForm = useSetAtom(
    locationGroupFormState.$updateLocationGroupForm,
  );
  const allFacilities = useAtomValue(locationsState.$rawLocationList);
  const saveLocationGroup = useSetAtom(locationsState.$saveLocationGroup);

  const facilityOptions = React.useMemo(
    () =>
      allFacilities.data?.map((facility) => ({
        key: facility.id,
        label: facility.name,
      })) ?? [],
    [allFacilities.data],
  );

  // Get selected facilities for default facility dropdown
  const selectedFacilityOptions = React.useMemo(
    () =>
      facilityOptions.filter((facility) =>
        form.facilityIds.includes(facility.key),
      ),
    [facilityOptions, form.facilityIds],
  );

  const hasChanges = React.useMemo(() => {
    const group = currentFacilityGroup?.data;
    if (!group) return false;

    const baselineFacilityIds = group.facilities?.map((f) => f.id) ?? [];
    const baselineDefaultFacilityId =
      group.defaultFacilityId ?? group.facilities?.[0]?.id;

    if (form.name !== (group.name || '')) return true;
    if (
      form.facilityIds.length !== baselineFacilityIds.length ||
      form.facilityIds.some((id) => !baselineFacilityIds.includes(id))
    )
      return true;
    if ((form.defaultFacilityId ?? '') !== (baselineDefaultFacilityId ?? ''))
      return true;

    return false;
  }, [form, currentFacilityGroup?.data]);

  const initializedGroupId = React.useRef<string | undefined>(undefined);
  const originalFacilityIdsRef = React.useRef<string[]>([]);

  const handleFieldChange =
    <K extends keyof typeof form>(field: K) =>
    (value: unknown) => {
      updateForm({
        [field]: value as (typeof form)[K],
      });
    };

  const handleDiscard = () => {
    if (currentFacilityGroup?.data) {
      const group = currentFacilityGroup.data;
      updateForm({
        name: group.name || '',
        facilityIds: group.facilities?.map((f) => f.id) ?? [],
        defaultFacilityId: group.facilities?.[0]?.id,
      });
    }
  };

  const handleSave = async () => {
    if (!currentFacilityGroup?.data?.id) return;

    try {
      await saveLocationGroup({
        originalFacilityIds: originalFacilityIdsRef.current,
        form: {
          name: form.name,
          facilityIds: form.facilityIds,
          defaultFacilityId: form.defaultFacilityId,
        },
      });

      // Update the original ref after successful save
      originalFacilityIdsRef.current = [...form.facilityIds];

      toast.success('Group updated successfully');
    } catch (error) {
      console.error('Failed to update facility group:', error);
      toast.error('Failed to update group');
    }
  };

  // Initialize form from currentFacilityGroup
  React.useEffect(() => {
    if (!currentFacilityGroup?.data || !groupId) return;

    const currentGroupId = currentFacilityGroup.data.id;
    if (initializedGroupId.current !== currentGroupId) {
      const group = currentFacilityGroup.data;
      const facilityIds = group.facilities?.map((f) => f.id) ?? [];
      updateForm({
        name: group.name || '',
        facilityIds,
        defaultFacilityId: group.defaultFacilityId ?? group.facilities?.[0]?.id,
      });
      originalFacilityIdsRef.current = [...facilityIds];
      initializedGroupId.current = currentGroupId;
    }
  }, [groupId, currentFacilityGroup?.data?.id]);

  return (
    <AsyncBoundary
      loading={currentFacilityGroup?.isLoading ?? !currentFacilityGroup?.data}
      error={currentFacilityGroup?.error}
      className="@LocationGroupInfo h-full"
    >
      <Col className={'@LocationGroupInfo h-full'}>
        <Col gap6 className={'h-0 grow overflow-y-auto'}>
          <SettingsContent size="medium" className="gap-6 p-5">
            {/* Facility Logo Section */}
            {/* <Row gap3>
            <Row
              xCenter
              yCenter
              className={
                'aspect-square h-full shrink-0 rounded-lg bg-primary/10'
              }
            >
              LOGO
            </Row>
            <Col gap3>
              <Col>
                <div className="text-sm font-semibold">Facility Logo</div>
                <div className="text-sm text-neutral/60">
                  Upload PNG or JPEG under 5MB
                </div>
              </Col>
              <Row gap2>
                <Button
                  variant="outline"
                  color="primary"
                  icon={<LucideUpload className="size-4" />}
                  isDisabled
                >
                  Update logo
                </Button>
                <Button
                  variant="outline"
                  color="failure"
                  icon={<LucideTrash2 className="size-4" />}
                  isDisabled
                />
              </Row>
            </Col>
          </Row> */}

            {/* Facility Group Info Section */}
            <FormSection icon={LucideBuilding} title="Facility Group Info">
              <Col gap4>
                <TextField
                  label="Group Name"
                  value={form.name}
                  onChange={handleFieldChange('name')}
                  isRequired
                />

                <MultiSelect
                  required
                  label="Facilities"
                  options={facilityOptions}
                  selectedKeys={form.facilityIds}
                  onSelectionChange={(keys) =>
                    handleFieldChange('facilityIds')(keys)
                  }
                  placeholder="Select facilities..."
                />

                <Select
                  label="Default Facility"
                  value={form.defaultFacilityId}
                  onChange={(value) =>
                    handleFieldChange('defaultFacilityId')(value || undefined)
                  }
                  options={selectedFacilityOptions}
                  placeholder="Select..."
                  isDisabled={selectedFacilityOptions.length === 0}
                />
              </Col>
            </FormSection>
          </SettingsContent>
        </Col>
        {/* Action Buttons */}
        <SettingsContent size="medium" className={'border-t border-neutral/15'}>
          <Row xEnd gap2 className={'p-5'}>
            <Button
              variant="outline"
              onPress={handleDiscard}
              isDisabled={!hasChanges}
            >
              Discard
            </Button>
            <Button
              variant="solid"
              color="primary"
              onPress={handleSave}
              isDisabled={!hasChanges}
            >
              Save updates
            </Button>
          </Row>
        </SettingsContent>
      </Col>
    </AsyncBoundary>
  );
};
